import React, { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button, IconButton, Snackbar } from '@mui/material'
import SnackbarContent from '@mui/material/SnackbarContent'
import { styled, Theme } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'

import { useTypedSelector } from '../../reducers'
import { setMemberReport } from '../../reducers/users'

type MikeTheme = Theme & {
  palette: Theme['palette'] & {
    ultimate?: { main: string; contrastText: string }
  }
}

const StyledSnackbarContent = styled(SnackbarContent)(({ theme }) => {
  const t = theme as MikeTheme
  return {
    backgroundColor: t.palette.ultimate?.main,
    color: t.palette.ultimate?.contrastText
  }
})

const USERS_PATH = '/site-access/users'

// The dialog's open flag lives in redux but the dialog only renders on the
// Users page, so leaving that page (e.g. Back while a report loads) closes it;
// otherwise the "ready" snack would never show. Only a change away from the
// page counts, so View (navigate + open) and StrictMode remounts are safe.
export const useCloseMemberReportOnLeave = () => {
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const previousPath = useRef(pathname)

  useEffect(() => {
    if (previousPath.current === USERS_PATH && pathname !== USERS_PATH) {
      dispatch(setMemberReport({ dialogOpen: false }))
    }
    previousPath.current = pathname
  }, [pathname, dispatch])
}

// Announces a report that finished generating while its dialog was closed.
const MemberReportReadySnack = () => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const ready = useTypedSelector(state => state['users'].memberReport.ready)
  useCloseMemberReportOnLeave()

  const onView = () => {
    navigate(USERS_PATH)
    dispatch(setMemberReport({ dialogOpen: true, ready: false }))
  }

  return (
    <Snackbar
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      open={ready}
    >
      <StyledSnackbarContent
        message={intl.formatMessage({ id: 'memberReport.ready' })}
        action={
          <>
            <Button color="inherit" size="small" onClick={onView}>
              {intl.formatMessage({ id: 'memberReport.view' })}
            </Button>
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => dispatch(setMemberReport({ ready: false }))}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        }
      />
    </Snackbar>
  )
}

export default MemberReportReadySnack
